import { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '../../graphql';
import { useExtension } from '../useExtension';
import { teamContext, teamIdContext, userContext } from './team.context';
import {
  getRecentTeamIdsStorageKey,
  parseStoredRecentTeamIds,
  upsertRecentTeamId,
} from './team.session.utils';

export function useMe() {
  let user = useContext(userContext);
  if (!user) user = {} as any;
  return user;
}

export function useTeam() {
  const tenant = useContext(teamContext);

  return useMemo(
    () =>
      [
        tenant.team,
        tenant.refetchTeam,
        tenant.isFetchingTeam,
        tenant.queryKey,
        tenant.queryError,
      ] as const,
    [
      tenant.team,
      tenant.refetchTeam,
      tenant.isFetchingTeam,
      tenant.queryKey,
      tenant.queryError,
    ]
  );
}

export function useTeamRole() {
  const tenant = useContext(teamContext);

  return {
    teamRole: tenant.role,
    isTeamAdmin: tenant.isTeamAdmin,
    isPartnerAdmin: tenant.isPartnerAdmin,
    isSuperAdmin: tenant.isSuperAdmin,
    isVrpAdmin: tenant.isVrpAdmin,
  };
}

export function useTeamSwitch() {
  const { sendMessage } = useExtension();
  const goto = useNavigate();
  const queryClient = useQueryClient();
  const tenant = useContext(teamIdContext);
  const { id: userId } = useMe();

  const setTeamId: (value: string | null, path?: string) => Promise<void> =
    useCallback(
      async (teamId: string | null, path?: string) => {
        if (teamId !== tenant?.teamId) {
          tenant?.setIsTeamSwitching(true);
          const shouldRefetch = (query: {
            options?: { meta?: Record<string, unknown> };
          }) => query.options?.meta?.teamScoped !== false;
          await queryClient
            .cancelQueries({ type: 'active', predicate: shouldRefetch })
            .catch(() => undefined);
        }

        if (teamId && userId) {
          const storageKey = getRecentTeamIdsStorageKey(userId);
          const recentTeamIds = parseStoredRecentTeamIds(
            globalThis.localStorage?.getItem(storageKey) ?? null
          );
          const nextRecentTeamIds = upsertRecentTeamId({
            teamId,
            recentTeamIds,
          });

          globalThis.localStorage?.setItem(
            storageKey,
            JSON.stringify(nextRecentTeamIds)
          );
        }

        tenant?.setTeamId(teamId);

        const isLinkSharing = window.location.search?.includes('sti=');

        if (!isLinkSharing && !path) {
          if (
            window.location.pathname.startsWith('/reports/statement-summaries')
          ) {
            goto('/reports/statement-summaries', { replace: true });
          } else if (window.location.pathname.includes('statement')) {
            goto('/statements', { replace: true });
          } else if (window.location.pathname === '/reservations') {
            goto('/reservations', { replace: true });
          } else if (window.location.pathname.startsWith('/reconciliation')) {
            goto('/reconciliation');
          } else if (window.location.pathname.startsWith('/deposit')) {
            goto('/deposits');
          } else if (window.location.pathname.startsWith('/expense')) {
            goto('/expenses');
          } else if (window.location.pathname.startsWith('/listing')) {
            goto('/listings');
          } else if (window.location.pathname.startsWith('/contact')) {
            goto('/contacts');
          } else if (window.location.pathname.startsWith('/owner')) {
            goto('/owners');
          } else if (window.location.pathname.startsWith('/connection')) {
            goto('/connections');
          } else if (window.location.pathname.startsWith('/fees-commission')) {
            goto('/fees-commissions');
          } else if (window.location.pathname.startsWith('/dashboard')) {
            goto('/');
          }
        }

        if (path) {
          goto(path);
        }

        try {
          sendMessage({ message: 'team_changed', data: { teamId } });
        } catch (error) {
          console.error(error);
        }
      },
      [
        goto,
        sendMessage,
        tenant?.teamId,
        userId,
        queryClient,
        tenant?.setTeamId,
        tenant?.setIsTeamSwitching,
      ]
    );

  return setTeamId;
}
